import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZSTUDIO — Studio créatif IA de Zifek
// Couche d'accès aux données + appels de génération.
//   - Contenu éditorial (copies, hashtags, slogans, campagnes, storyboards,
//     prompts) : fonction edge « zstudio ».
//   - Média réel (image, vidéo, voix) : fonction edge « content-studio »
//     (xAI Grok Imagine) — réutilisée pour éviter toute duplication.
// ─────────────────────────────────────────────────────────────

export const ZSTUDIO_FUNCTIONS_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL as string}/functions/v1/zstudio`;

export type ZKind = 'image' | 'video' | 'ad' | 'social' | 'campaign' | 'fashion' | 'product' | 'service';
export type ZStatus = 'draft' | 'generating' | 'ready' | 'error';

export interface ZStudioProject {
  id: number;
  owner_id: number | null;
  name: string;
  kind: string;
  status: string;
  style: string | null;
  aspect_ratio: string;
  language: string;
  source_type: string | null;
  source_ref: string | null;
  brief: string | null;
  branding: Record<string, unknown> | null;
  meta: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface ZStudioCreation {
  id: number;
  owner_id: number | null;
  project_id: number | null;
  kind: string;
  media_kind: string | null;
  url: string | null;
  thumbnail_url: string | null;
  title: string | null;
  description: string | null;
  content: Record<string, unknown> | null;
  status: string;
  provider: string | null;
  favorite: boolean;
  created_at: string;
}

export interface ZStudioGeneration {
  id: number;
  owner_id: number | null;
  project_id: number | null;
  creation_id: number | null;
  type: string | null;
  prompt: string | null;
  model: string | null;
  provider: string | null;
  output: Record<string, unknown> | null;
  status: string;
  credits: number | null;
  created_at: string;
}

// ── Référentiels de choix créatifs ──
export const Z_KINDS: { value: ZKind; label: string; icon: string; hint: string }[] = [
  { value: 'image', label: 'Image', icon: 'ri-image-line', hint: 'Visuel professionnel' },
  { value: 'video', label: 'Vidéo', icon: 'ri-film-line', hint: 'Courte vidéo IA' },
  { value: 'ad', label: 'Publicité', icon: 'ri-megaphone-line', hint: 'Campagne publicitaire' },
  { value: 'social', label: 'Publication sociale', icon: 'ri-share-circle-line', hint: 'Post réseaux sociaux' },
  { value: 'campaign', label: 'Campagne complète', icon: 'ri-rocket-line', hint: 'Concept + médias + textes' },
  { value: 'fashion', label: 'Contenu de mode', icon: 'ri-t-shirt-line', hint: 'Mannequin, défilé' },
  { value: 'product', label: 'Contenu produit', icon: 'ri-shopping-bag-3-line', hint: 'Depuis un produit Zifek' },
  { value: 'service', label: 'Contenu service', icon: 'ri-service-line', hint: 'Depuis un service Zifek' },
];

export const Z_STYLES = ['Studio', 'Défilé', 'Éditorial', 'Streetwear', 'Luxe', 'Traditionnel', 'Contemporain', 'Mariage', 'Casual', 'Professionnel'];
export const Z_ENVIRONMENTS = ['Studio', 'Rue', 'Hôtel', 'Plage', 'Désert', 'Architecture marocaine', 'Boutique', 'Podium', 'Événement'];
export const Z_POSES = ['Debout', 'Assis', 'Marche', 'Portrait', 'Trois-quarts', 'Dos', 'Mouvement'];
export const Z_MODEL_TYPES = ['Femme', 'Homme', 'Enfant'];
export const Z_OBJECTIVES = ['Faire connaître', 'Générer des ventes', 'Obtenir des réservations', 'Présenter un nouveau produit', 'Lancement', 'Promotion', 'Événement', 'Notoriété'];
export const Z_SEASONS = ['Aucune', 'Ramadan', 'Aïd', 'Rentrée', 'Été', 'Hiver', 'Saint-Valentin', 'Black Friday', 'Lancement de collection'];
export const Z_PLATFORMS = ['Instagram', 'Facebook', 'TikTok', 'YouTube', 'LinkedIn', 'X', 'WhatsApp'];
export const Z_ASPECTS: { value: string; label: string; hint: string }[] = [
  { value: '9:16', label: 'Vertical 9:16', hint: 'TikTok / Reels / Stories' },
  { value: '1:1', label: 'Carré 1:1', hint: 'Instagram / Facebook' },
  { value: '16:9', label: 'Paysage 16:9', hint: 'YouTube / site web' },
];

export function zAspectDimensions(aspect: string): { width: number; height: number } {
  if (aspect === '1:1') return { width: 1024, height: 1024 };
  if (aspect === '16:9') return { width: 1280, height: 720 };
  return { width: 720, height: 1280 };
}

export const Z_MEDIA_KIND_META: Record<string, { label: string; icon: string; className: string }> = {
  image: { label: 'Image', icon: 'ri-image-line', className: 'bg-accent-100 text-accent-700' },
  video: { label: 'Vidéo', icon: 'ri-film-line', className: 'bg-primary-100 text-primary-700' },
  text: { label: 'Texte', icon: 'ri-file-text-line', className: 'bg-secondary-100 text-secondary-800' },
  lookbook: { label: 'Lookbook', icon: 'ri-book-open-line', className: 'bg-secondary-100 text-secondary-800' },
  tryon: { label: 'Try-on', icon: 'ri-user-star-line', className: 'bg-accent-100 text-accent-700' },
};

// ── Qualité & format (specs 59 & 60) ──
export const Z_QUALITIES: { value: string; label: string; hint: string }[] = [
  { value: 'draft', label: 'Brouillon', hint: 'Rapide, économique' },
  { value: 'standard', label: 'Standard', hint: 'Bon compromis' },
  { value: 'high', label: 'Haute qualité', hint: 'Rendu optimal' },
];

export function zQualityToApi(quality: string): { quality: string; resolution: string } {
  if (quality === 'high') return { quality: 'medium', resolution: '2k' };
  if (quality === 'draft') return { quality: 'low', resolution: '1k' };
  return { quality: 'medium', resolution: '1k' };
}

export const Z_IMAGE_FORMATS = ['JPG', 'PNG', 'WebP'];
export const Z_VIDEO_FORMATS = ['MP4'];

export function zFormatFromDataUri(dataUri: string): string {
  const m = dataUri.match(/^data:image\/([a-z0-9]+)/i);
  return m ? m[1].toLowerCase() : 'png';
}

/** Extension adaptée au format d'image demandé. */
export function zImageExtension(format: string): string {
  const f = (format || 'PNG').toLowerCase();
  if (f === 'jpg' || f === 'jpeg') return 'jpg';
  if (f === 'webp') return 'webp';
  return 'png';
}

// ── Personnalisation du mannequin (spec 9) ──
export const Z_MODEL_APPEARANCES = ['Générique', 'Élégant', 'Sportif', 'Minimaliste', 'Classique', 'Audacieux'];
export const Z_MODEL_HAIR = ['Noir lisse', 'Brune ondulée', 'Blonde', 'Courte', 'Bouclée', 'Châtain', 'Auburn'];
export const Z_MODEL_SKIN = ['Claire', 'Méditerranéenne', 'Mate', 'Dorée', 'Foncée', 'Métissée'];
export const Z_MODEL_BODY = ['Fine', 'Moyenne', 'Athlétique', 'Courbes', 'Grande'];
export const Z_MODEL_EXPRESSIONS = ['Neutre', 'Souriant', 'Sérieux', 'Confiant', 'Doux'];

// ── Actions guidées de l'assistant (spec 51) ──
export const Z_ASSISTANT_ACTION_MAP: Record<string, { label: string; path: string }> = {
  create_image: { label: 'Créer une image', path: '/dashboard/zstudio/wizard?type=image&mode=image' },
  create_video: { label: 'Créer une vidéo', path: '/dashboard/zstudio/wizard?type=video&mode=video' },
  create_ad: { label: 'Créer une publicité', path: '/dashboard/zstudio/wizard?type=ad&mode=ad' },
  create_social_post: { label: 'Créer une publication', path: '/dashboard/zstudio/wizard?type=social&mode=social' },
  generate_campaign: { label: 'Campagne complète', path: '/dashboard/zstudio/wizard?type=campaign&mode=campaign' },
  sketch_to_image: { label: 'Transformer un croquis', path: '/dashboard/zstudio/wizard?mode=sketch' },
  try_on: { label: 'Essayage virtuel', path: '/dashboard/zstudio/tryon' },
  lookbook: { label: 'Lookbook IA', path: '/dashboard/zstudio/lookbook' },
  edit_image: { label: 'Éditer une image', path: '/dashboard/zstudio/edit' },
  select_product: { label: 'Choisir un produit', path: '/dashboard/zstudio/wizard?type=product&mode=product' },
  select_service: { label: 'Choisir un service', path: '/dashboard/zstudio/wizard?type=service&mode=service' },
};

// ── Projets ──
export async function listZProjects(ownerId: number): Promise<ZStudioProject[]> {
  const { data, error } = await supabase
    .from('zstudio_projects')
    .select('*')
    .eq('owner_id', ownerId)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ZStudioProject[];
}

export async function getZProject(id: number): Promise<ZStudioProject | null> {
  const { data, error } = await supabase.from('zstudio_projects').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return (data as ZStudioProject) || null;
}

export async function createZProject(params: {
  ownerId: number;
  name: string;
  kind?: string;
  style?: string;
  aspectRatio?: string;
  language?: string;
  sourceType?: string;
  sourceRef?: string;
  brief?: string;
  branding?: Record<string, unknown>;
  meta?: Record<string, unknown>;
}): Promise<ZStudioProject> {
  const { data, error } = await supabase
    .from('zstudio_projects')
    .insert({
      owner_id: params.ownerId,
      name: params.name.slice(0, 140),
      kind: params.kind || 'image',
      status: 'draft',
      style: params.style || null,
      aspect_ratio: params.aspectRatio || '9:16',
      language: params.language || 'fr',
      source_type: params.sourceType || null,
      source_ref: params.sourceRef || null,
      brief: params.brief || null,
      branding: params.branding || null,
      meta: params.meta || null,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return data as ZStudioProject;
}

export async function updateZProject(id: number, patch: Partial<ZStudioProject>): Promise<void> {
  const { error } = await supabase
    .from('zstudio_projects')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteZProject(id: number): Promise<void> {
  await supabase.from('zstudio_creations').delete().eq('project_id', id);
  const { error } = await supabase.from('zstudio_projects').delete().eq('id', id);
  if (error) throw error;
}

// ── Créations ──
export async function listZCreations(ownerId: number): Promise<ZStudioCreation[]> {
  const { data, error } = await supabase
    .from('zstudio_creations')
    .select('*')
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ZStudioCreation[];
}

export async function listProjectCreations(projectId: number): Promise<ZStudioCreation[]> {
  const { data, error } = await supabase
    .from('zstudio_creations')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ZStudioCreation[];
}

export async function createZCreation(params: {
  ownerId: number;
  projectId?: number | null;
  kind: string;
  mediaKind?: string;
  url?: string;
  thumbnailUrl?: string;
  title?: string;
  description?: string;
  content?: Record<string, unknown>;
  status?: string;
  provider?: string;
}): Promise<ZStudioCreation | null> {
  const { data, error } = await supabase
    .from('zstudio_creations')
    .insert({
      owner_id: params.ownerId,
      project_id: params.projectId ?? null,
      kind: params.kind,
      media_kind: params.mediaKind ?? null,
      url: params.url ?? null,
      thumbnail_url: params.thumbnailUrl ?? null,
      title: params.title ?? null,
      description: params.description ?? null,
      content: params.content ?? null,
      status: params.status || 'ready',
      provider: params.provider ?? null,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ZStudioCreation) || null;
}

export async function updateZCreation(id: number, patch: Partial<ZStudioCreation>): Promise<void> {
  const { error } = await supabase.from('zstudio_creations').update(patch).eq('id', id);
  if (error) throw error;
}

export async function toggleZFavorite(id: number, favorite: boolean): Promise<void> {
  const { error } = await supabase.from('zstudio_creations').update({ favorite }).eq('id', id);
  if (error) throw error;
}

export async function deleteZCreation(id: number): Promise<void> {
  const { error } = await supabase.from('zstudio_creations').delete().eq('id', id);
  if (error) throw error;
}

// ── Historique des générations ──
export async function listZGenerations(ownerId: number, limit = 40): Promise<ZStudioGeneration[]> {
  const { data, error } = await supabase
    .from('zstudio_generations')
    .select('*')
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as ZStudioGeneration[];
}

export async function logZGeneration(params: {
  ownerId: number;
  projectId?: number | null;
  creationId?: number | null;
  type: string;
  prompt?: string;
  model?: string;
  provider?: string;
  output?: Record<string, unknown>;
  status?: string;
  credits?: number;
}): Promise<void> {
  const { error } = await supabase.from('zstudio_generations').insert({
    owner_id: params.ownerId,
    project_id: params.projectId ?? null,
    creation_id: params.creationId ?? null,
    type: params.type,
    prompt: params.prompt ?? null,
    model: params.model ?? null,
    provider: params.provider ?? null,
    output: params.output ?? null,
    status: params.status || 'done',
    credits: params.credits ?? null,
  });
  if (error) throw error;
}

// ── Aide à la relance d'une génération (historique / mini-panneaux) ──

/** Catégorie générique d'un type de génération (image, vidéo, pub, social, campagne). */
export function zGenerationKind(type?: string | null): string {
  const t = (type || '').toLowerCase();
  if (t.includes('video')) return 'video';
  if (t.includes('campaign')) return 'campaign';
  if (t.includes('ad') || t.includes('slogan')) return 'ad';
  if (t.includes('hashtag') || t.includes('social') || t.includes('caption')) return 'social';
  return 'image';
}

/** Studio spécialisé correspondant au type de génération (ou null si générique). */
export function zGenerationStudioPath(type?: string | null): string | null {
  const t = (type || '').toLowerCase();
  if (t === 'tryon') return '/dashboard/zstudio/tryon';
  if (t === 'edit_image' || t === 'edit') return '/dashboard/zstudio/edit';
  if (t === 'lookbook') return '/dashboard/zstudio/lookbook';
  return null;
}

/**
 * Chemin pour relancer une génération précédente : on rouvre le studio
 * spécialisé (Try-on / Édition IA / Lookbook) ou, sinon, le wizard pré-rempli.
 */
export function zGenerationReusePath(g: { type?: string | null; prompt?: string | null }): string {
  const studio = zGenerationStudioPath(g.type);
  if (studio) return studio;
  const kind = zGenerationKind(g.type);
  const search = new URLSearchParams();
  search.set('type', kind);
  search.set('mode', kind);
  if (g.prompt) {
    search.set('subject', g.prompt.slice(0, 110));
    search.set('brief', g.prompt);
  }
  return `/dashboard/zstudio/wizard?${search.toString()}`;
}

/** Chemin du wizard pré-rempli pour relancer une création générique à l'identique. */
export function zGenerationWizardPath(g: { type?: string | null; prompt?: string | null }): string {
  const kind = zGenerationKind(g.type);
  const search = new URLSearchParams();
  search.set('type', kind);
  search.set('mode', kind);
  if (g.prompt) {
    search.set('subject', g.prompt.slice(0, 110));
    search.set('brief', g.prompt);
  }
  return `/dashboard/zstudio/wizard?${search.toString()}`;
}

/** Duplique un projet ZStudio (copie indépendante pour repartir à l'identique). */
export async function duplicateZProject(id: number): Promise<ZStudioProject | null> {
  const p = await getZProject(id);
  if (!p) return null;
  return createZProject({
    ownerId: p.owner_id ?? 0,
    name: `${p.name} (copie)`,
    kind: p.kind,
    style: p.style || undefined,
    aspectRatio: p.aspect_ratio,
    language: p.language,
    sourceType: p.source_type || undefined,
    sourceRef: p.source_ref || undefined,
    brief: p.brief || undefined,
    branding: p.branding || undefined,
    meta: p.meta || undefined,
  });
}

// ── Média réel (via content-studio / xAI Grok Imagine) ──

/** Génère une image IA et la rend durable (lien permanent). */
export async function generateZImage(params: {
  prompt: string;
  n?: number;
  aspectRatio?: string;
  quality?: string;
  resolution?: string;
  persist?: boolean;
  folder?: string;
}): Promise<string[]> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: {
      action: 'generate_image',
      prompt: params.prompt,
      n: params.n || 1,
      aspectRatio: params.aspectRatio,
      quality: params.quality,
      resolution: params.resolution,
    },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  const urls: string[] = Array.isArray(data?.urls) ? (data.urls as string[]) : [];
  if (!params.persist || urls.length === 0) return urls;
  const folder = params.folder || 'zstudio/images';
  const permanent: string[] = [];
  for (let i = 0; i < urls.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    permanent.push(await persistZMedia(urls[i], `zstudio-image-${Date.now()}-${i}.png`, folder, 'image/png'));
  }
  return permanent;
}

/**
 * Édition d'image IA (spec 44) : modifie une image existante à partir d'une
 * consigne en langage naturel, via xAI /v1/images/edits (1 à 5 images de réf.).
 * Conserve l'image d'origine — un nouveau fichier durable est créé.
 */
export async function editZImage(params: {
  prompt: string;
  images: string[];
  aspectRatio?: string;
  quality?: string;
  resolution?: string;
  persist?: boolean;
  folder?: string;
}): Promise<string[]> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: {
      action: 'edit_image',
      prompt: params.prompt,
      images: params.images,
      aspectRatio: params.aspectRatio,
      quality: params.quality,
      resolution: params.resolution,
    },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  const urls: string[] = Array.isArray(data?.urls) ? (data.urls as string[]) : [];
  if (!params.persist || urls.length === 0) return urls;
  const folder = params.folder || 'zstudio/edits';
  const permanent: string[] = [];
  for (let i = 0; i < urls.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    permanent.push(await persistZMedia(urls[i], `zstudio-edit-${Date.now()}-${i}.png`, folder, 'image/png'));
  }
  return permanent;
}

/** Démarre une génération vidéo (image-to-video si une image est fournie). */
export async function startZVideo(params: {
  prompt: string;
  duration?: number;
  aspectRatio?: string;
  image?: string;
}): Promise<{ requestId: string; provider: string }> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: { action: 'generate_scene_video', prompt: params.prompt, duration: params.duration || 5, aspectRatio: params.aspectRatio, image: params.image },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return { requestId: String(data?.request_id || ''), provider: String(data?.provider || 'xAI') };
}

export async function pollZVideo(requestId: string): Promise<{ status: string; videoUrl: string | null; duration: number | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: { action: 'poll_video', request_id: requestId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return {
    status: String(data?.status || 'pending'),
    videoUrl: data?.videoUrl ?? null,
    duration: data?.duration ?? null,
    error: data?.error ?? null,
  };
}

/** Récupère un média xAI temporaire et le ré-héberge (lien permanent). */
export async function persistZMedia(url: string, filename: string, folder = 'zstudio/media', contentType = 'application/octet-stream'): Promise<string> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: { action: 'persist_media', url, filename, folder, contentType },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return String(data?.url || '');
}

// ── Contenu éditorial (via la fonction edge « zstudio ») ──

export interface ZFactsheet {
  name?: string;
  kind?: string;
  category?: string;
  price?: string;
  description?: string;
  url?: string;
  features?: string[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function invokeZstudio(payload: Record<string, unknown>): Promise<any> {
  const { data, error } = await supabase.functions.invoke('zstudio', { body: payload });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return data;
}

export interface ZAdCopy {
  caption: string;
  short_text: string;
  long_text: string;
  call_to_action: string;
  per_platform: Record<string, { caption: string; hashtags: string[] }>;
  provider?: string;
}

export async function zAdCopy(params: {
  subject: string;
  factsheet?: ZFactsheet;
  objective?: string;
  tone?: string;
  platforms?: string[];
  language?: string;
}): Promise<ZAdCopy> {
  const data = await invokeZstudio({ action: 'ad_copy', ...params });
  return {
    caption: String(data?.caption || ''),
    short_text: String(data?.short_text || ''),
    long_text: String(data?.long_text || ''),
    call_to_action: String(data?.call_to_action || ''),
    per_platform: data?.per_platform || {},
    provider: data?.provider,
  };
}

export async function zHashtags(params: { subject: string; factsheet?: ZFactsheet; location?: string; count?: number; language?: string }): Promise<string[]> {
  const data = await invokeZstudio({ action: 'hashtags', ...params });
  return Array.isArray(data?.hashtags) ? (data.hashtags as string[]) : [];
}

export async function zSlogans(params: { subject: string; factsheet?: ZFactsheet; tone?: string; count?: number; language?: string }): Promise<string[]> {
  const data = await invokeZstudio({ action: 'slogan', ...params });
  return Array.isArray(data?.slogans) ? (data.slogans as string[]) : [];
}

export interface ZStoryboardScene {
  scene: number;
  description: string;
  camera: string;
  transition: string;
  duration: number;
}

export interface ZCampaign {
  concept: string;
  slogan: string;
  caption: string;
  hashtags: string[];
  cta: string;
  storyboard: ZStoryboardScene[];
  image_prompt: string;
  video_prompt: string;
  variants: string[];
  provider?: string;
}

export async function zCampaign(params: {
  subject: string;
  factsheet?: ZFactsheet;
  objective?: string;
  platforms?: string[];
  season?: string;
  style?: string;
  language?: string;
}): Promise<ZCampaign> {
  const data = await invokeZstudio({ action: 'campaign', ...params });
  return {
    concept: String(data?.concept || ''),
    slogan: String(data?.slogan || ''),
    caption: String(data?.caption || ''),
    hashtags: Array.isArray(data?.hashtags) ? (data.hashtags as string[]) : [],
    cta: String(data?.cta || ''),
    storyboard: Array.isArray(data?.storyboard) ? (data.storyboard as ZStoryboardScene[]) : [],
    image_prompt: String(data?.image_prompt || ''),
    video_prompt: String(data?.video_prompt || ''),
    variants: Array.isArray(data?.variants) ? (data.variants as string[]) : [],
    provider: data?.provider,
  };
}

export async function zSketchPrompt(params: {
  description: string;
  modelType?: string;
  style?: string;
  environment?: string;
  pose?: string;
  language?: string;
}): Promise<{ image_prompt: string; preserved_details: string[] }> {
  const data = await invokeZstudio({ action: 'sketch_prompt', ...params });
  return {
    image_prompt: String(data?.image_prompt || ''),
    preserved_details: Array.isArray(data?.preserved_details) ? (data.preserved_details as string[]) : [],
  };
}

export async function zGarmentPrompt(params: {
  description: string;
  modelType?: string;
  style?: string;
  environment?: string;
  pose?: string;
  language?: string;
}): Promise<{ image_prompt: string; preserved_details: string[] }> {
  const data = await invokeZstudio({ action: 'garment_prompt', ...params });
  return {
    image_prompt: String(data?.image_prompt || ''),
    preserved_details: Array.isArray(data?.preserved_details) ? (data.preserved_details as string[]) : [],
  };
}

export async function zLookbookCopy(params: {
  collection: string;
  items: { ref?: string; name?: string; price?: string }[];
  language?: string;
}): Promise<{ title: string; intro: string; pages: { ref: string; title: string; description: string }[] }> {
  const data = await invokeZstudio({ action: 'lookbook_copy', ...params });
  return {
    title: String(data?.title || ''),
    intro: String(data?.intro || ''),
    pages: Array.isArray(data?.pages) ? (data.pages as { ref: string; title: string; description: string }[]) : [],
  };
}

// ── Campagnes (spec 69) ──

export interface ZStudioCampaign {
  id: number;
  owner_id: number | null;
  project_id: number | null;
  name: string | null;
  subject: string | null;
  kind: string | null;
  objective: string | null;
  season: string | null;
  platforms: string[] | null;
  status: string;
  assets: Record<string, unknown> | null;
  content: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export async function listZCampaigns(ownerId: number): Promise<ZStudioCampaign[]> {
  const { data, error } = await supabase
    .from('zstudio_campaigns')
    .select('*')
    .eq('owner_id', ownerId)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ZStudioCampaign[];
}

export async function createZCampaign(params: {
  ownerId: number;
  projectId?: number | null;
  name?: string;
  subject?: string;
  objective?: string;
  season?: string;
  platforms?: string[];
  status?: string;
  assets?: Record<string, unknown>;
  content?: Record<string, unknown>;
}): Promise<ZStudioCampaign> {
  const { data, error } = await supabase
    .from('zstudio_campaigns')
    .insert({
      owner_id: params.ownerId,
      project_id: params.projectId ?? null,
      name: params.name ?? null,
      subject: params.subject ?? null,
      kind: 'campaign',
      objective: params.objective ?? null,
      season: params.season ?? null,
      platforms: params.platforms ?? [],
      status: params.status || 'draft',
      assets: params.assets ?? null,
      content: params.content ?? null,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return data as ZStudioCampaign;
}

export async function updateZCampaign(id: number, patch: Partial<ZStudioCampaign>): Promise<void> {
  const { error } = await supabase
    .from('zstudio_campaigns')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteZCampaign(id: number): Promise<void> {
  const { error } = await supabase.from('zstudio_campaigns').delete().eq('id', id);
  if (error) throw error;
}

// ── Assistant ZStudio AI (spec 51) ──

export interface ZAssistantTurn { role: 'user' | 'assistant'; content: string; }
export interface ZAssistantReply { reply: string; suggestions: string[]; actions: string[]; provider?: string; }

export async function zAssistantChat(params: {
  messages: ZAssistantTurn[];
  context?: string;
  language?: string;
}): Promise<ZAssistantReply> {
  const data = await invokeZstudio({ action: 'assistant', ...params });
  return {
    reply: String(data?.reply || ''),
    suggestions: Array.isArray(data?.suggestions) ? (data.suggestions as string[]) : [],
    actions: Array.isArray(data?.actions) ? (data.actions as string[]) : [],
    provider: data?.provider,
  };
}

// ── Product / service Zifek ──

// ── Produits / services Zifek (pour le wizard) ──

export interface ZProductItem {
  id: number;
  name: string;
  price: number | null;
  currency: string | null;
  description: string | null;
  media: { url?: string; type?: string }[] | null;
  category_id: number | null;
}

export async function listZProducts(): Promise<ZProductItem[]> {
  const { data, error } = await supabase
    .from('product_items')
    .select('id,name,price,currency,description,media,category_id,status')
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(60);
  if (error) throw error;
  return (data || []) as ZProductItem[];
}

export function firstProductImage(media: ZProductItem['media']): string | null {
  if (!Array.isArray(media)) return null;
  const img = media.find((m) => m?.url && (!m.type || m.type === 'image')) || media.find((m) => m?.url);
  return img?.url || null;
}

export interface ZServiceItem {
  id: number;
  name: string;
  price: number | null;
  description: string | null;
  image: string | null;
  category: string | null;
}

export async function listZServices(): Promise<ZServiceItem[]> {
  const { data, error } = await supabase.from('services').select('*').limit(60);
  if (error) return [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data || []).map((s: any) => ({
    id: Number(s.id),
    name: String(s.nom || s.name || s.titre || 'Service'),
    price: s.prix != null ? Number(s.prix) : (s.price != null ? Number(s.price) : null),
    description: s.description || s.desc || null,
    image: s.image || s.photo || null,
    category: s.categorie || s.category || null,
  }));
}